import { Link } from "react-router-dom";
import { CheckCircle, Star, Phone, ArrowRight, Shield, Eye, Smile, HeartHandshake } from "lucide-react";
import { LimitedTimeOfferSection } from "../components/LimitedTimeOfferSection";
import { WhyChooseUsSection } from "../components/WhyChooseUsSection";
import { FAQSection } from "../components/FAQSection";
import { BenefitsGuideTeaser } from "../components/BenefitsGuideSection";
import { CarriersSection } from "../components/CarriersSection";
import { usePageMeta } from "../hooks/usePageMeta";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1758873271761-6cfe9b4f000c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBkaXZlcnNlJTIwdGVhbSUyMG9mZmljZXxlbnwwfHx8fDE3NzI3MjU1NTR8MA&ixlib=rb-4.1.0&q=85",
  handshake: "https://images.unsplash.com/photo-1745847768380-2caeadbb3b71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxoYW5kc2hha2UlMjBidXNpbmVzcyUyMGRlYWwlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc3MjcyNTU1N3ww&ixlib=rb-4.1.0&q=85",
};

const benefits = [
  "Affordable employee benefits",
  "Easy enrollment process",
  "Personalized one on one service",
  "Local North Dakota support",
];

const testimonials = [
  { quote: "They're super easy to work with and offer very affordable policies.", name: "Steven Burckhard", stars: 5 },
  { quote: "This group did a great job and were very patient with my employees and I.", name: "Doug", stars: 5 },
  { quote: "Working with Joey and Levi has been a great experience.", name: "Colleen Severinson", stars: 5 },
];

const services = [
  { icon: Smile, title: "Dental Insurance", desc: "Comprehensive dental coverage for your entire team." },
  { icon: Eye, title: "Vision Insurance", desc: "Affordable vision plans for glasses and eye care." },
  { icon: Shield, title: "Supplemental Insurance", desc: "Extra protection for life's unexpected moments." },
  { icon: HeartHandshake, title: "Life & Disability", desc: "Group life, short term and long term disability coverage." },
];

// Sub-components
const StarRow = ({ size = 16 }) => (
  <div className="flex space-x-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} className="text-yellow-400" fill="#FBBF24" stroke="none" />
    ))}
  </div>
);

const ServiceCard = ({ service }) => (
  <div
    data-testid={`service-card-${service.title}`}
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all p-6"
  >
    <div className="w-12 h-12 bg-[#EAF9F9] rounded-xl flex items-center justify-center mb-4">
      <service.icon size={24} className="text-[#2EC4C4]" />
    </div>
    <h3 className="font-semibold text-lg text-[#1A2744] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {service.title}
    </h3>
    <p className="text-[#475569] text-sm leading-relaxed">{service.desc}</p>
  </div>
);

const TestimonialCard = ({ testimonial }) => (
  <div
    data-testid={`testimonial-card-${testimonial.name}`}
    className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-shadow"
  >
    <StarRow size={16} />
    <p className="text-[#475569] italic my-4 leading-relaxed">"{testimonial.quote}"</p>
    <p className="font-semibold text-[#1A2744]">— {testimonial.name}</p>
  </div>
);

const heroPoints = [
  "No cost to the employer",
  "Easy enrollment, handled for you",
  "One on one, local service year round",
  "Built for small and mid size North Dakota businesses",
];

const HeroSection = () => (
  <section data-testid="hero-section" className="bg-[#F8FAFC] py-16 lg:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <div className="inline-flex items-center space-x-2 bg-[#EAF9F9] text-[#2EC4C4] px-4 py-2 rounded-full text-sm font-semibold">
            <Star size={14} fill="#2EC4C4" stroke="none" />
            <span>5.0 Star Rated Agency, North Dakota</span>
          </div>
          <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl text-[#1A2744] leading-tight" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.02em" }}>
            Great Employee Benefits{" "}
            <span className="text-[#2EC4C4]">At Zero Cost to You</span>
          </h1>
          <p className="text-[#475569] text-lg leading-relaxed">
            Give your team the dental, vision, and life coverage they actually want, without adding a line to your budget. We're a Minot based, independent benefits team that shops the whole market to build a package that fits your business.
          </p>
          <ul className="space-y-3">
            {heroPoints.map((point) => (
              <li key={point} className="flex items-center space-x-3">
                <CheckCircle size={20} className="text-[#2EC4C4] flex-shrink-0" />
                <span className="text-[#0F172A] font-medium">{point}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <Link to="/contact" data-testid="hero-quote-btn" className="inline-flex items-center justify-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-8 py-4 text-base transition-all shadow-md hover:shadow-lg active:scale-95">
              <span>Get a Free Quote</span><ArrowRight size={18} />
            </Link>
            <Link to="/contact" data-testid="hero-consult-btn" className="inline-flex items-center justify-center bg-white border-2 border-[#1A2744] text-[#1A2744] hover:bg-[#F0F3F9] font-semibold rounded-lg px-8 py-4 text-base transition-all">
              Schedule a Consultation
            </Link>
          </div>
          <a href="tel:+17017217993" data-testid="hero-phone" className="inline-flex items-center space-x-2 text-[#1A2744] font-medium hover:text-[#2EC4C4] transition-colors">
            <Phone size={18} className="text-[#2EC4C4]" /><span>(701) 721-7993</span>
          </a>
        </div>
        <div className="relative">
          <div className="rounded-[32px] rounded-tl-none overflow-hidden shadow-2xl">
            <img src={IMAGES.hero} alt="Professional business team" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A2744]/10 to-transparent rounded-[32px] rounded-tl-none" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 shadow-xl border border-gray-100">
            <div className="flex items-center space-x-3">
              <StarRow size={16} />
              <div>
                <p className="font-bold text-[#1A2744] text-sm">5.0 Rating</p>
                <p className="text-xs text-gray-500">Google Reviews</p>
              </div>
            </div>
          </div>
          <div data-testid="hero-claims-stat" className="absolute -top-5 -right-3 bg-[#1A2744] rounded-2xl px-5 py-4 shadow-xl">
            <p className="font-bold text-[#2EC4C4] text-2xl leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>$650,000+</p>
            <p className="text-xs text-gray-300 mt-1.5">Colonial Life claims paid to our clients this year</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const WhatWeDoSection = () => (
  <section data-testid="what-we-do-section" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img src={IMAGES.handshake} alt="Business partnership" className="w-full h-[380px] object-cover" />
        </div>
        <div className="space-y-6">
          <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Your Trusted Benefits Partner in North Dakota
          </h2>
          <p className="text-[#475569] text-base leading-relaxed">
            At Viall Business Solutions, we help small businesses access the same quality benefits packages that large corporations offer, without the complexity or high costs.
          </p>
          <p className="text-[#475569] text-base leading-relaxed">
            Based in Minot, we work directly with you to find affordable solutions that keep employees happy and your bottom line healthy.
          </p>
          <div className="space-y-3">
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-center space-x-3">
                <CheckCircle size={20} className="text-[#2EC4C4] flex-shrink-0" />
                <span className="text-[#0F172A] font-medium">{benefit}</span>
              </div>
            ))}
          </div>
          <Link to="/about" data-testid="learn-more-btn" className="inline-flex items-center space-x-2 text-[#2EC4C4] font-semibold hover:text-[#25A0A0] transition-colors">
            <span>Learn More About Us</span><ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const ServicesPreviewSection = () => (
  <section data-testid="services-preview-section" className="py-20 bg-[#F8FAFC]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Insurance Solutions Built for Small Business
        </h2>
        <p className="text-[#475569] text-lg max-w-2xl mx-auto">
          From dental to group life and disability coverage, we offer protection your employees will appreciate.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {services.map((service) => <ServiceCard key={service.title} service={service} />)}
      </div>
      <div className="text-center mt-10">
        <Link to="/services" data-testid="view-all-services-btn" className="inline-flex items-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-8 py-3 transition-all shadow-md hover:shadow-lg active:scale-95">
          <span>View All Services</span><ArrowRight size={18} />
        </Link>
      </div>
    </div>
  </section>
);

const ReviewsSnippetSection = () => (
  <section data-testid="reviews-section" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4"><StarRow size={24} /></div>
        <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          What Our Clients Say
        </h2>
        <p className="text-[#475569] text-lg">5.0 stars on Google — trusted by North Dakota businesses</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {testimonials.map((t) => <TestimonialCard key={t.name} testimonial={t} />)}
      </div>
      <div className="text-center mt-10">
        <Link to="/reviews" data-testid="view-all-reviews-btn" className="inline-flex items-center space-x-2 text-[#2EC4C4] font-semibold hover:text-[#25A0A0] transition-colors">
          <span>Read More Reviews</span><ArrowRight size={18} />
        </Link>
      </div>
    </div>
  </section>
);

const CTABannerSection = () => (
  <section data-testid="cta-section" className="py-20 bg-[#1A2744]">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-bold text-3xl lg:text-4xl text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Ready to Enhance Your Employee Benefits?
      </h2>
      <p className="text-gray-300 text-lg mb-8">Get a free, no obligation quote today. Let us show you how affordable great benefits can be.</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/contact" data-testid="cta-quote-btn" className="inline-flex items-center justify-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-8 py-4 text-base transition-all shadow-md hover:shadow-lg active:scale-95">
          <span>Get a Free Quote</span><ArrowRight size={18} />
        </Link>
        <a href="tel:+17017217993" data-testid="cta-call-btn" className="inline-flex items-center justify-center space-x-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1A2744] font-semibold rounded-lg px-8 py-4 text-base transition-all">
          <Phone size={18} /><span>Call (701) 721-7993</span>
        </a>
      </div>
    </div>
  </section>
);

export default function HomePage() {
  usePageMeta({
    title: "Employee Benefits & Small Business Insurance in North Dakota | Viall Business Solutions",
    description: "Give your employees great benefits at zero cost to you. Dental, vision, group life, and disability for North Dakota small businesses. Local, independent, 5-star rated.",
    path: "/",
  });
  return (
    <div className="pt-16 lg:pt-20">
      <HeroSection />
      <CarriersSection />
      <WhatWeDoSection />
      <ServicesPreviewSection />
      <WhyChooseUsSection />
      <LimitedTimeOfferSection />
      <BenefitsGuideTeaser />
      <FAQSection />
      <ReviewsSnippetSection />
      <CTABannerSection />
      <a href="tel:+17017217993" data-testid="mobile-call-fab" className="fixed bottom-6 right-6 lg:hidden bg-[#2EC4C4] text-white rounded-full p-4 shadow-xl hover:bg-[#25A0A0] transition-colors z-40" aria-label="Call us">
        <Phone size={24} />
      </a>
    </div>
  );
}
